import React, { useEffect, useState } from 'react';
import { KeyRound, Link2, RotateCw, CheckCircle2, AlertTriangle, XCircle, Percent } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, CardHeader, Note, RubyModal } from '../../components/finance/financeUi';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import {
  estateAdminApi, type StripeConfigStatus, type StripeTestResult, type StripeConfigKey,
} from '../../services/estateAdminApi';
import { useStaffAccess } from '../../hooks/useStaffAccess';
import { stripeSettingsErrorMessage, OWNER_ONLY_MESSAGE } from './stripeSettingsErrors';
import { parseFeeDraft, feeConfigToFields, BLANK_FEE_FIELDS, type FeeDraftFields } from './feeConfigDraft';
import { FeeFieldsEditor } from './FeeFieldsEditor';
import { money } from '../../components/finance/financeFormat';

/** dev/staging run test keys only; prod runs live keys only — the banner the brief asks for. */
function requiredMode(): { stage: 'dev' | 'staging' | 'prod'; label: string } {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.includes('admin.rubytill.com') && !h.includes('dev') && !h.includes('stage')) {
    return { stage: 'prod', label: 'Production — live keys only' };
  }
  if (h.includes('stage')) return { stage: 'staging', label: 'Staging — test keys only' };
  return { stage: 'dev', label: 'Development — test keys only' };
}

const STATUS_DOT: Record<'ok' | 'warn' | 'bad', React.ReactNode> = {
  ok: <CheckCircle2 size={14} className="text-emerald-600" />,
  warn: <AlertTriangle size={14} className="text-amber-600" />,
  bad: <XCircle size={14} className="text-red-600" />,
};

/** A write-only secret field: never pre-filled, cleared immediately after a successful save. */
const SecretField: React.FC<{
  label: string;
  status?: { set: boolean; tail: string | null; mode: 'test' | 'live' | null; updatedAt: string | null; updatedBy: string | null; source: string } | null;
  onSave: (value: string) => Promise<void>;
  requiredModeLabel: string;
  /** Final review B-I3: only the Owner may replace a value (server: 403 owner_only). */
  canEdit: boolean;
}> = ({ label, status, onSave, requiredModeLabel, canEdit }) => {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusLine = !status
    ? 'Loading…'
    : status.source === 'shared'
      ? 'Using shared key'
      : status.set
        ? `Set · ends in …${status.tail || '????'}${status.mode ? ` · ${status.mode} mode` : ''}${status.updatedBy ? ` · updated by ${status.updatedBy}` : ''}${status.updatedAt ? ` on ${new Date(status.updatedAt).toLocaleDateString()}` : ''}`
        : 'Not set';

  const save = async () => {
    if (!value.trim()) { setError('Enter a value to replace it.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(value.trim());
      setValue('');
      setEditing(false);
    } catch (e: any) {
      setError(stripeSettingsErrorMessage(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-stone-800">{label}</div>
          <div className="text-xs text-stone-500 mt-0.5">{statusLine}</div>
          <div className="text-[11px] text-stone-400 mt-0.5">{requiredModeLabel}</div>
        </div>
        {!editing && canEdit && (
          <button
            type="button"
            onClick={() => { setEditing(true); setValue(''); setError(null); }}
            className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
          >
            Replace
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="New value — never pre-filled"
            className="flex-1 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setValue(''); setError(null); }}
            className="px-4 py-2.5 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
          >
            Cancel
          </button>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </Card>
  );
};

/**
 * Final review C-I3: the platform fallback fee is NOT a secret -- show the current values and edit
 * them as real numbers (the same strict fields as the fee-plan editor), sending the object shape
 * the server expects. Owner-only, like every Stripe settings write.
 */
const FallbackFeeCard: React.FC<{
  status: StripeConfigStatus['feeConfig'];
  canEdit: boolean;
  onSave: (cfg: NonNullable<ReturnType<typeof parseFeeDraft>['config']>) => Promise<void>;
}> = ({ status, canEdit, onSave }) => {
  const [draft, setDraft] = useState<FeeDraftFields | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = draft ? parseFeeDraft(draft) : null;
  const v = status.value;
  const current = v
    ? `ACH ${(v.achBps / 100).toFixed(2)}% + ${money(v.achFlatCents)} · Card ${(v.cardBps / 100).toFixed(2)}% + ${money(v.cardFlatCents)} · Min ${money(v.minCents)} · ${v.maxCents === null ? 'No max' : `Max ${money(v.maxCents)}`}`
    : 'Not set — the built-in default applies';
  const meta = `${status.updatedBy ? `Updated by ${status.updatedBy}` : ''}${status.updatedAt ? ` on ${new Date(status.updatedAt).toLocaleDateString()}` : ''}`;

  const save = async () => {
    if (!parsed?.config) { setError('Fix the highlighted fields before saving.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(parsed.config);
      setDraft(null);
    } catch (e: any) {
      setError(stripeSettingsErrorMessage(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-stone-800 flex items-center gap-1.5"><Percent size={14} /> Fallback fee config</div>
          <div className="text-xs text-stone-600 mt-0.5">{current}</div>
          {meta && <div className="text-[11px] text-stone-400 mt-0.5">{meta}</div>}
        </div>
        {!draft && canEdit && (
          <button
            type="button"
            onClick={() => { setDraft(v ? feeConfigToFields(v) : { ...BLANK_FEE_FIELDS }); setError(null); }}
            className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
          >
            Edit
          </button>
        )}
      </div>
      {draft && (
        <div className="mt-3 space-y-3">
          <FeeFieldsEditor value={draft} onChange={setDraft} errors={parsed?.errors ?? {}} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy || !parsed?.config}
              className="px-4 py-2.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setDraft(null); setError(null); }}
              className="px-4 py-2.5 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </Card>
  );
};

const EstateStripeSettingsPage: React.FC = () => {
  // Final review B-I3: Stripe settings EDIT is owner-level (spec §6a); the server enforces it
  // (403 owner_only) and the controls are hidden for everyone else.
  const { isOwner } = useStaffAccess();
  const [status, setStatus] = useState<StripeConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<StripeTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [rotateConfirm, setRotateConfirm] = useState(false);

  const mode = requiredMode();

  const load = () => {
    setLoading(true);
    estateAdminApi.stripeConfig()
      .then(setStatus)
      .catch((e) => setError(e?.message || 'Could not load Stripe settings'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveKey = async (key: Exclude<StripeConfigKey, 'feeConfig'>, value: string) => {
    const updated = await estateAdminApi.updateStripeConfig({ [key]: value });
    setStatus(updated);
  };
  const saveFeeConfig = async (feeConfig: NonNullable<ReturnType<typeof parseFeeDraft>['config']>) => {
    setStatus(await estateAdminApi.updateStripeConfig({ feeConfig }));
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await estateAdminApi.testStripeConfig());
    } catch (e: any) {
      setError(e?.message || 'Could not run the connection test');
    } finally {
      setTesting(false);
    }
  };

  const createWebhook = async () => {
    setWebhookBusy(true);
    try {
      const r = await estateAdminApi.ensureStripeWebhook(false);
      setStatus(r);
    } catch (e: any) {
      setError(stripeSettingsErrorMessage(e, 'Could not create the webhook'));
    } finally {
      setWebhookBusy(false);
    }
  };

  const rotateWebhook = async () => {
    setWebhookBusy(true);
    try {
      const r = await estateAdminApi.ensureStripeWebhook(true);
      setStatus(r);
      setRotateConfirm(false);
    } catch (e: any) {
      setError(stripeSettingsErrorMessage(e, 'Could not rotate the webhook'));
    } finally {
      setWebhookBusy(false);
    }
  };

  const toneOf = (ok: boolean, warn = false): 'ok' | 'warn' | 'bad' => (ok ? 'ok' : warn ? 'warn' : 'bad');

  return (
    <InventoryPage icon={KeyRound} title="Stripe settings" subtitle="The keys and webhook Ruby Estate charges through" crumb="Ruby Estate">
      <div className="mb-4 px-4 py-2.5 rounded-lg bg-stone-900 text-white text-xs font-semibold inline-flex items-center gap-2">
        <KeyRound size={14} /> {mode.label}
      </div>

      {!isOwner && <Note tone="info">{OWNER_ONLY_MESSAGE}. You can view the status and run the connection test.</Note>}
      {error && <Note tone="danger">{error}</Note>}

      {loading || !status ? (
        <RubyLoader variant="page" label="Loading Stripe settings" />
      ) : (
        <div className="space-y-4">
          <SecretField label="Secret key" status={status.secretKey} requiredModeLabel={mode.label} canEdit={isOwner} onSave={(v) => saveKey('secretKey', v)} />
          <SecretField label="Publishable key" status={status.publishableKey} requiredModeLabel={mode.label} canEdit={isOwner} onSave={(v) => saveKey('publishableKey', v)} />
          <SecretField label="Connect webhook secret" status={status.webhookSecret} requiredModeLabel={mode.label} canEdit={isOwner} onSave={(v) => saveKey('webhookSecret', v)} />
          <FallbackFeeCard status={status.feeConfig} canEdit={isOwner} onSave={saveFeeConfig} />

          <Card>
            <CardHeader
              icon={<Link2 size={16} />}
              title="Connect webhook"
              hint={status.webhook.url || 'No endpoint yet'}
              actions={isOwner ? (
                <div className="flex gap-2">
                  {!status.webhook.endpointId ? (
                    <button
                      type="button"
                      onClick={createWebhook}
                      disabled={webhookBusy}
                      className="px-3 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40 min-h-[44px]"
                    >
                      {webhookBusy ? 'Working…' : 'Create webhook'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRotateConfirm(true)}
                      disabled={webhookBusy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 disabled:opacity-40 min-h-[44px]"
                    >
                      <RotateCw size={13} /> Rotate webhook
                    </button>
                  )}
                </div>
              ) : undefined}
            />
            <div className="p-4 text-sm text-stone-700">
              Status: {status.webhook.status || 'none'}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Test connection"
              actions={(
                <button
                  type="button"
                  onClick={runTest}
                  disabled={testing}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black text-white text-xs font-semibold hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
                >
                  {testing ? <RubyGemMark size={14} drawing color="currentColor" /> : null}
                  {testing ? 'Testing…' : 'Test connection'}
                </button>
              )}
            />
            {testResult && (
              <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  {STATUS_DOT[testResult.secretKey === 'ok' ? 'ok' : testResult.secretKey === 'unverified' ? 'warn' : 'bad']}
                  Secret key: {testResult.secretKey}
                </div>
                <div className="flex items-center gap-2">
                  {STATUS_DOT[toneOf(testResult.connect === 'ok', testResult.connect === 'list_only' || testResult.connect === 'create_refused')]}
                  Connect: {testResult.connect}{testResult.connectHint ? ` (${testResult.connectHint})` : ''}
                </div>
                <div className="flex items-center gap-2">
                  {STATUS_DOT[toneOf(testResult.publishableKey === 'ok')]}
                  Publishable key: {testResult.publishableKey}
                </div>
                <div className="flex items-center gap-2">
                  {STATUS_DOT[toneOf(testResult.webhook === 'ok')]}
                  Webhook: {testResult.webhook}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      <RubyModal
        open={rotateConfirm}
        icon={<KeyRound size={16} style={{ color: '#DC143C' }} />}
        title="Rotate the Connect webhook?"
        confirmLabel="Rotate webhook"
        busy={webhookBusy}
        onClose={() => setRotateConfirm(false)}
        onConfirm={rotateWebhook}
      >
        <p className="text-amber-700 font-medium">
          The current endpoint will be deleted from Stripe and replaced with a new one.
        </p>
        <p>Any integration relying on the old endpoint id stops receiving events immediately.</p>
      </RubyModal>
    </InventoryPage>
  );
};

export default EstateStripeSettingsPage;
